import useApiService from '~/composables/api/useApiService';
import { apiEndpoints } from '~/constants';
import { useWalletDocumentsStore } from '~/stores/wallets/wallet-documents-store';
import { useWalletSDDMandatStore } from '~/stores/wallets/wallet-sdd-mandat-store';
import { useWalletsStore } from '~/stores/wallets/wallets-store';
import type {
  ApiError,
  TurnoverHistoryItem,
  Kpi,
  BankAccountDetails,
  VirtualIbanBody,
  AddUBOBodyType,
  PaymentBodyType,
  UpdateDocumentStatusBodyType,
  AddSDDMandatBodyType,
  AddSDDMandatResponseType,
  SddMandateInfosType,
  BlockUnblockWalletBodyType,
  AddWalletBodyType,
  UpdateWalletBodyType,
  DisableBodyType,
  EnableBodyType,
  UpdateUBOBodyType,
  RequestDebitBodyType,
  WalletDocumentInfosType,
  EditBankStatusBodyType,
  LogCommentBodyType,
  EditWalletStatusBodyType,
  WireTransferBodyType,
  GenerateKycLinkBodyType,
  WalletApiResponse,
  GetWalletResponse,
  WalletTransactionsResponseType,
  WalletUbosResponseType,
  BankAccountRulesType,
  PostDocumentResponseType,
  PostWalletResponseType,
  VirtualIbanResponse,
  PostLogCommentResponseType,
  PostWireBankTransferResponseType,
  PostIdentLinkResponseType,
  PutBlockUnblockWalletResponseType,
  PutWalletResponseType,
  PutDisableResponseType
} from '~/types';

export default function useWalletsApi() {
  const { get, post, put } = useApiService();
  const { selectedDocument } = storeToRefs(useWalletDocumentsStore());
  const { getAddSDDmandatBody } = storeToRefs(useWalletSDDMandatStore());
  const { userInfos, virtualIBANBody } = storeToRefs(useWalletsStore());

  const getWallet = async (walletIntId: number): Promise<GetWalletResponse> => {
    const { data, error } = (await get(`${apiEndpoints.wallets}/${walletIntId}`, 'getWallet')) as {
      data: { value: GetWalletResponse };
      error: { value: any };
    };

    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const getWalletByExtId = async (queryParams: string): Promise<WalletApiResponse> => {
    const { data, error } = await get(`${apiEndpoints.wallets}?${queryParams}`);
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as WalletApiResponse;
  };

  const getWalletThresholds = async (walletIntId: number): Promise<any> => {
    const { data, error } = await get(
      `${apiEndpoints.wallets}/${walletIntId}${apiEndpoints.thresholds}`,
      'getThresholds'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const getWalletLastTransactions = async (
    walletIntId: number,
    queryParams: string
  ): Promise<WalletTransactionsResponseType> => {
    const { data, error } = await get(
      `${apiEndpoints.wallets}/${walletIntId}${apiEndpoints.transactions}?${queryParams}`
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as WalletTransactionsResponseType;
  };

  const getWalletStatement = async (params: string) => {
    const { data, error } = await get(
      `${apiEndpoints.reports}/financial${params}`,
      'getWalletStatement'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const getWalletUbos = async (queryParams: string): Promise<WalletUbosResponseType> => {
    const { data, error } = await get(
      `${apiEndpoints.wallets}/${userInfos.value?.intId}${apiEndpoints.ultimatebeneficialowners}?${queryParams}`,
      'getUbos'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as WalletUbosResponseType;
  };

  const getWalletDocuments = async (): Promise<WalletDocumentInfosType[]> => {
    const { data, error } = await get(
      `${apiEndpoints.wallets}/${userInfos.value?.intId}${apiEndpoints.documents}`,
      'getWalletDocuments'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as WalletDocumentInfosType[];
  };

  const getBankAccounts = async (walletIntId: number) => {
    const { data, error } = await get(
      `${apiEndpoints.wallets}/${walletIntId}${apiEndpoints.ibans}`
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as BankAccountDetails[];
  };

  const getBankAccountRules = async (country: string): Promise<BankAccountRulesType> => {
    const { data, error } = await get(`${apiEndpoints.bankaccountrules}?country=${country}`);
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as BankAccountRulesType;
  };

  const getKpiData = async () => {
    const { data, error } = await get(
      `${apiEndpoints.wallets}/11${apiEndpoints.statistics}`,
      'getKpiData'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as Kpi;
  };

  const getTurnoverHistoryData = async () => {
    const { data, error } = await get(`${apiEndpoints.chartdatas}/turnoverlast12months`);
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return (data.value as TurnoverHistoryItem[]).map((rawData: TurnoverHistoryItem) => ({
      ...rawData,
      StartDate: rawData.Month
    })) as TurnoverHistoryItem[];
  };

  const getFile = async () => {
    const { data, error } = await get(
      `${apiEndpoints.files}/${selectedDocument.value?.id}`,
      'getFile',
      {
        responseType: 'arrayBuffer'
      }
    );
    if (error.value) {
      if (error.value.statusCode === 500) {
        throw new Error(error.value.message);
      } else {
        throw new Error((error.value.cause as ApiError).Message);
      }
    }
    return data.value;
  };

  const getSddMandates = async (walletIntId: number): Promise<SddMandateInfosType[]> => {
    const { data, error } = await get(
      `${apiEndpoints.wallets}/${walletIntId}${apiEndpoints.sddmandates}`,
      'loadingSdds'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as SddMandateInfosType[];
  };

  const getVirtualIbansDocument = async (language: string, virtualIbanId: string): Promise<any> => {
    const { data, error } = await get(
      `${apiEndpoints.virtualibans}/${virtualIbanId}/pdf?language=${language}`,
      undefined,
      { responseType: 'arrayBuffer' }
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const getRelatedTransactions = async (documentId: number): Promise<ArrayBuffer> => {
    const { data, error } = await get(`${apiEndpoints.files}/${documentId}`, undefined, {
      responseType: 'arrayBuffer'
    });
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as ArrayBuffer;
  };

  const postAddBankAccount = async (url: string, body: any) => {
    const { data, error } = await post(url, body);
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const postAddUBO = async (body: AddUBOBodyType) => {
    const { data, error } = await post(apiEndpoints.ultimatebeneficialowners, body, 'addUBO');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const postAddPayment = async (body: PaymentBodyType) => {
    const { data, error } = await post(apiEndpoints.paymentforms, body, 'addPayment');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const postSDDMandat = async (): Promise<AddSDDMandatResponseType> => {
    const body: AddSDDMandatBodyType = getAddSDDmandatBody.value;
    const { data, error } = await post(apiEndpoints.sddmandates, body, 'addSDDMandate');

    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as AddSDDMandatResponseType;
  };

  const postDocument = async (body: FormData): Promise<PostDocumentResponseType> => {
    const { data, error } = await post(apiEndpoints.files, body, 'uploadDocument');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PostDocumentResponseType;
  };

  const postWallet = async (body: AddWalletBodyType): Promise<PostWalletResponseType> => {
    const { data, error } = await post(apiEndpoints.wallets, body, 'addWallet');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PostWalletResponseType;
  };

  const postRequestDebit = async (body: RequestDebitBodyType): Promise<any> => {
    const { data, error } = await post(`${apiEndpoints.transactions}/in/sdd`, body, 'requestDebit');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const postVirtualIban = async (): Promise<VirtualIbanResponse> => {
    const { data, error } = await post(
      apiEndpoints.virtualibans,
      virtualIBANBody,
      'generateVirtualIban'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as VirtualIbanResponse;
  };

  const postLogComment = async (body: LogCommentBodyType): Promise<PostLogCommentResponseType> => {
    const { data, error } = await post(apiEndpoints.activitylogs, body, 'addLogComment');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PostLogCommentResponseType;
  };

  const postWireBankTransfer = async (
    body: WireTransferBodyType
  ): Promise<PostWireBankTransferResponseType> => {
    const { data, error } = await post(
      `${apiEndpoints.transactions}/out`,
      body,
      'wireBankTransfer'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PostWireBankTransferResponseType;
  };

  const postIdentLink = async (
    body: GenerateKycLinkBodyType
  ): Promise<PostIdentLinkResponseType> => {
    const { data, error } = await post('/Enrolment', body, 'generateKycLink');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PostIdentLinkResponseType;
  };

  const putDisableVirtualIban = async (id: number, body: VirtualIbanBody) => {
    const { data, error } = await put(`ibans/${id}/disable`, body);
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const putDocumentStatus = async (body: UpdateDocumentStatusBodyType) => {
    const { data, error } = await put(
      `${apiEndpoints.documents}/${selectedDocument.value?.id}/status`,
      body,
      'updateDocumentStatus'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data;
  };

  const putBlockUnblockWallet = async (
    walletIntId: number,
    body: BlockUnblockWalletBodyType
  ): Promise<PutBlockUnblockWalletResponseType> => {
    const { data, error } = await put(
      `${apiEndpoints.wallets}/${walletIntId}/blockStatus`,
      body,
      'blockUnblockWallet'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PutBlockUnblockWalletResponseType;
  };

  const putWallet = async (body: UpdateWalletBodyType): Promise<PutWalletResponseType> => {
    const { data, error } = await put(
      `${apiEndpoints.wallets}/${userInfos.value?.intId}/detail`,
      body,
      'updateWallet'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PutWalletResponseType;
  };

  const putWalletStatus = async (
    walletIntId: number,
    body: EditWalletStatusBodyType
  ): Promise<PutWalletResponseType> => {
    const { data, error } = await put(
      `${apiEndpoints.wallets}/${walletIntId}/status`,
      body,
      'editWalletStatus'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PutWalletResponseType;
  };

  const putDisable = async (
    type: string,
    id: string,
    body: DisableBodyType
  ): Promise<PutDisableResponseType> => {
    const { data, error } = await put(`${apiEndpoints[type]}/${id}/disable`, body, 'disabling');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value as PutDisableResponseType;
  };

  const putEnable = async (type: string, id: string, body: EnableBodyType): Promise<any> => {
    const { data, error } = await put(`${apiEndpoints[type]}/${id}/status`, body, 'enabling');
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const putWalletUBO = async (UBOId: number, body: UpdateUBOBodyType): Promise<any> => {
    const { data, error } = await put(
      `${apiEndpoints.ultimatebeneficialowners}/${UBOId}`,
      body,
      'updateUBO'
    );
    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  const putBankAccountStatus = async (
    bankId: string,
    body: EditBankStatusBodyType
  ): Promise<any> => {
    const { data, error } = await put(
      `${apiEndpoints.ibans}/${bankId}/status`,
      body,
      'editBankAccountStatus'
    );

    if (error.value) throw new Error((error.value.cause as ApiError).Message);
    return data.value;
  };

  return {
    getWallet,
    getWalletByExtId,
    getWalletThresholds,
    getWalletLastTransactions,
    getWalletStatement,
    getWalletUbos,
    getWalletDocuments,
    getBankAccounts,
    getBankAccountRules,
    getKpiData,
    getTurnoverHistoryData,
    getFile,
    getSddMandates,
    getVirtualIbansDocument,
    getRelatedTransactions,
    postAddBankAccount,
    postAddUBO,
    postAddPayment,
    postSDDMandat,
    postDocument,
    postWallet,
    postRequestDebit,
    postVirtualIban,
    postLogComment,
    postWireBankTransfer,
    postIdentLink,
    putDisableVirtualIban,
    putDocumentStatus,
    putBlockUnblockWallet,
    putWallet,
    putWalletStatus,
    putDisable,
    putEnable,
    putWalletUBO,
    putBankAccountStatus
  };
}
